import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ScrollView } from 'react-native';
import { useLocation } from 'react-router-dom';

const DeveloperTools = () => {
  const [isVisible, setIsVisible] = useState(false);

  const location = useLocation();

  // Only show in development mode
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const pages = [
    { name: 'Welcome', path: '/welcome', emoji: '👋' },
    { name: 'Onboarding', path: '/onboarding', emoji: '📝' },
    { name: 'Dashboard', path: '/', emoji: '🏠' },
    { name: 'Income', path: '/income', emoji: '💵' },
    { name: 'Spending', path: '/spending', emoji: '💸' },
    { name: 'Budget', path: '/budget', emoji: '📊' },
    { name: 'Accounts', path: '/accounts', emoji: '🏦' },
    { name: 'Transactions', path: '/transactions', emoji: '📄' },
    { name: 'Protection', path: '/protection', emoji: '🛡️' },
    { name: 'Assets', path: '/assets', emoji: '📘' },
    { name: 'Liabilities', path: '/liabilities', emoji: '📕' },
    { name: 'Cash Flow', path: '/cashflow', emoji: '💚' },
    { name: 'Wealth Plan', path: '/wealth-plan', emoji: '📈' },
    { name: 'Fact Finder', path: '/fact-finder', emoji: '📝' },
    { name: 'Fact Finder Summary', path: '/fact-finder-summary', emoji: '📋' },
    ...(process.env.NODE_ENV === 'development' ? [{ name: 'Knowledge Admin', path: '/knowledge-admin', emoji: '🧠' }] : []),
  ];

  const handlePageNavigation = (path) => {
    // Handle routing logic properly for developer tools
    if (path === '/welcome' || path === '/onboarding') {
      // For welcome/onboarding pages, remove onboarding completion
      localStorage.removeItem('zecher_onboarding_completed');
      localStorage.removeItem('zecher_onboarding_answers');
    } else {
      // For main app pages, mark onboarding as complete
      localStorage.setItem('zecher_onboarding_completed', 'true');
    }
    
    // Navigate with a force refresh to trigger proper routing
    window.location.href = path;
  };

  const resetOnboarding = () => {
    localStorage.removeItem('zecher_onboarding_completed');
    localStorage.removeItem('zecher_onboarding_answers');
    window.location.href = '/welcome';
  };

  const completeOnboarding = () => {
    localStorage.setItem('zecher_onboarding_completed', 'true');
    window.location.href = '/';
  };

  return (
    <>
      {/* Floating Developer Button */}
      <TouchableOpacity 
        style={styles.floatingButton}
        onPress={() => setIsVisible(true)}
      >
        <Text style={styles.floatingButtonText}>⚙️</Text>
      </TouchableOpacity>

      {/* Developer Tools Modal */}
      <Modal
        visible={isVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setIsVisible(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          onPress={() => setIsVisible(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>🛠 Developer Tools</Text>
              <Text style={styles.currentPage}>Current: {location.pathname}</Text>
            </View>

            <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={true}>
              {/* Quick Actions */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Quick Actions</Text>
                <View style={styles.actionButtons}>
                  <TouchableOpacity 
                    style={[styles.actionButton, styles.completeButton]}
                    onPress={completeOnboarding}
                  >
                    <Text style={styles.actionButtonText}>✅ Complete Onboarding</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={[styles.actionButton, styles.resetButton]}
                    onPress={resetOnboarding}
                  >
                    <Text style={styles.actionButtonText}>🔄 Reset Onboarding</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* Page Navigation */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Navigate to Page</Text>
                <View style={styles.pageGrid}>
                  {pages.map((page) => (
                    <TouchableOpacity
                      key={page.path}
                      style={[
                        styles.pageButton,
                        location.pathname === page.path && styles.pageButtonActive
                      ]}
                      onPress={() => handlePageNavigation(page.path)}
                    >
                      <Text style={styles.pageEmoji}>{page.emoji}</Text>
                      <Text style={[
                        styles.pageText,
                        location.pathname === page.path && styles.pageTextActive
                      ]}>
                        {page.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </ScrollView>

            {/* Close Button */}
            <TouchableOpacity 
              style={styles.closeButton}
              onPress={() => setIsVisible(false)}
            >
              <Text style={styles.closeButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  floatingButton: {
    position: 'fixed',
    top: 20,
    right: 20,
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#DC2626',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 1000,
  },
  floatingButtonText: {
    fontSize: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    maxWidth: 600,
    width: '100%',
    maxHeight: '80%',
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  currentPage: {
    fontSize: 14,
    color: '#6B7280',
    fontFamily: 'monospace',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  actionButton: {
    flex: 1,
    minWidth: '45%',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  completeButton: {
    backgroundColor: '#D1FAE5',
  },
  resetButton: {
    backgroundColor: '#FEE2E2',
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  scrollContainer: {
    flex: 1,
    maxHeight: 400,
  },
  pageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  pageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    minWidth: '45%',
  },
  pageButtonActive: {
    backgroundColor: '#EFF6FF',
    borderColor: '#2563EB',
  },
  pageEmoji: {
    fontSize: 16,
    marginRight: 8,
  },
  pageText: {
    fontSize: 14,
    color: '#374151',
    flex: 1,
  },
  pageTextActive: {
    color: '#2563EB',
    fontWeight: '600',
  },
  closeButton: {
    backgroundColor: '#F3F4F6',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  closeButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
  },
});

export default DeveloperTools; 